import React, { useState } from 'react';
import { bookingService } from '../api/services';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Search, ChevronDown, ChevronUp, Loader2, AlertCircle, RotateCcw,
  Calendar, Clock, Video, Mic, MessageCircle, User, X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const sessionIcon = { VIDEO: Video, AUDIO: Mic, CHAT: MessageCircle };

const STATUS_STYLE = {
  PENDING:     'bg-amber-50 text-amber-700 border-amber-200',
  CONFIRMED:   'bg-forest-50 text-forest-700 border-forest-200',
  COMPLETED:   'bg-blue-50 text-blue-700 border-blue-200',
  CANCELLED:   'bg-cream-100 text-forest-400 border-cream-200',
  RESCHEDULED: 'bg-purple-50 text-purple-700 border-purple-200',
};

// ── Reschedule modal — admin version, no 2x cap ──────────────
const AdminRescheduleModal = ({ booking, onClose, onDone }) => {
  const [scheduledAt, setScheduledAt] = useState('');
  const [loading, setLoading] = useState(false);
  const minDate = new Date(Date.now() + 3600000).toISOString().slice(0, 16);

  const handle = async (e) => {
    e.preventDefault();
    if (!scheduledAt) return;
    setLoading(true);
    try {
      await bookingService.reschedule(booking.id, new Date(scheduledAt).toISOString());
      toast.success('Booking rescheduled — both parties notified');
      onDone();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not reschedule');
    } finally { setLoading(false); }
  };

  const client = booking.clientProfile?.user;
  const expert = booking.expertProfile?.user;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-forest-900/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-forest-900">Reschedule Booking</h3>
          <button onClick={onClose} className="text-forest-400 hover:text-forest-700"><X size={18} /></button>
        </div>
        <p className="text-sm text-forest-500">
          {client?.firstName} {client?.lastName} with {expert?.firstName} {expert?.lastName}.
          {' '}As admin, this isn't limited by the usual 2-reschedule cap.
        </p>
        <form onSubmit={handle} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1.5">New Date & Time</label>
            <input type="datetime-local" required value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} min={minDate}
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm focus:outline-none focus:border-forest-500" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="flex-1 text-sm py-2.5 rounded-full border border-cream-200 text-forest-600 hover:border-forest-400 transition-colors">Cancel</button>
            <button type="submit" disabled={loading || !scheduledAt}
              className="flex-1 btn-primary justify-center text-sm py-2.5 disabled:opacity-60">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Reschedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Booking row ───────────────────────────────────────────────
const BookingRow = ({ b, onReschedule }) => {
  const [expanded, setExpanded] = useState(false);
  const client = b.clientProfile?.user;
  const expert = b.expertProfile?.user;
  const Icon = sessionIcon[b.sessionType] || Video;
  const canReschedule = ['PENDING', 'CONFIRMED'].includes(b.status);

  return (
    <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
      <div className="p-4 flex items-center gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="w-9 h-9 bg-forest-700 rounded-xl flex items-center justify-center text-cream-50 flex-shrink-0">
          <Icon size={15} />
        </div>
        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="min-w-0">
            <p className="text-sm font-medium text-forest-900 truncate">{client?.firstName} {client?.lastName}</p>
            <p className="text-[10px] text-forest-400 truncate">Client · {client?.email}</p>
          </div>
          <div className="min-w-0">
            <p className="text-sm text-forest-700 truncate">{expert?.firstName} {expert?.lastName}</p>
            <p className="text-[10px] text-forest-400 truncate">Expert · {expert?.email}</p>
          </div>
          <div className="min-w-0">
            <p className="text-xs text-forest-700 flex items-center gap-1"><Calendar size={11} /> {new Date(b.scheduledAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            <p className="text-[10px] text-forest-400 flex items-center gap-1 mt-0.5"><Clock size={10} /> {new Date(b.scheduledAt).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })} · {b.duration}min</p>
          </div>
        </div>
        <span className={`text-[10px] font-medium border px-2 py-0.5 rounded-full flex-shrink-0 ${STATUS_STYLE[b.status] ?? ''}`}>{b.status}</span>
        {expanded ? <ChevronUp size={15} className="text-forest-400 flex-shrink-0" /> : <ChevronDown size={15} className="text-forest-400 flex-shrink-0" />}
      </div>

      {expanded && (
        <div className="border-t border-cream-100 p-4 space-y-3 text-sm">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider">Booking ID</p>
              <p className="text-forest-800 font-mono text-xs">{b.id}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider">Requested</p>
              <p className="text-forest-800">{new Date(b.requestedAt).toLocaleDateString('en-GH', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider">Session Type</p>
              <p className="text-forest-800">{b.sessionType}</p>
            </div>
            <div>
              <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider">Reschedules</p>
              <p className="text-forest-800">{b.rescheduleCount ?? 0}</p>
            </div>
            {b.levelName && (
              <div>
                <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider">Level</p>
                <p className="text-forest-800">{b.levelName}</p>
              </div>
            )}
            {b.sessionFocus && (
              <div>
                <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider">Focus</p>
                <p className="text-forest-800">{b.sessionFocus}</p>
              </div>
            )}
          </div>

          {/* Client + Expert contact/context */}
          <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-cream-50">
            <div className="space-y-1">
              <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider">Client</p>
              {client?.phone && <p className="text-forest-800">{client.phone}</p>}
              {b.clientProfile?.businessName && (
                <p className="text-forest-600 text-xs">{b.clientProfile.businessName}{b.clientProfile.businessType ? ` · ${b.clientProfile.businessType}` : ''}</p>
              )}
              {b.clientProfile?.city && <p className="text-forest-400 text-xs">{b.clientProfile.city}</p>}
            </div>
            <div className="space-y-1">
              <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider">Expert</p>
              {expert?.phone && <p className="text-forest-800">{expert.phone}</p>}
              {b.expertProfile?.specializations && (
                <p className="text-forest-600 text-xs line-clamp-2">{b.expertProfile.specializations}</p>
              )}
              {b.expertProfile?.hourlyRate && (
                <p className="text-forest-400 text-xs">{b.expertProfile.currency} {b.expertProfile.hourlyRate}/hr</p>
              )}
            </div>
          </div>

          {b.cancelReason && (
            <div>
              <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider">Cancel Reason</p>
              <p className="text-forest-800">{b.cancelReason}</p>
            </div>
          )}
          {b.notes && (
            <div>
              <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider mb-1">Notes</p>
              <p className="text-forest-700 bg-cream-50 border border-cream-200 rounded-lg p-3 whitespace-pre-wrap">{b.notes}</p>
            </div>
          )}

          {/* Session detail, if the call was ever generated */}
          {b.session && (
            <div className="bg-cream-50 border border-cream-200 rounded-xl p-3 space-y-1">
              <p className="text-[10px] font-semibold text-forest-400 uppercase tracking-wider">Session</p>
              <p className="text-forest-800 text-xs">
                Status: <span className="font-medium">{b.session.status}</span>
                {b.session.expertMarkedComplete && ' · Marked complete by expert'}
              </p>
              {b.session.startedAt && (
                <p className="text-forest-500 text-xs">
                  Started {new Date(b.session.startedAt).toLocaleString('en-GH', { dateStyle: 'medium', timeStyle: 'short' })}
                  {b.session.endedAt && ` — Ended ${new Date(b.session.endedAt).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}`}
                </p>
              )}
              {b.session.recordingUrl && (
                <a href={b.session.recordingUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-forest-600 underline">
                  Watch session recording →
                </a>
              )}
            </div>
          )}

          {canReschedule && (
            <button onClick={() => onReschedule(b)}
              className="flex items-center gap-1.5 text-xs font-medium border border-forest-200 text-forest-600 hover:bg-forest-50 px-3 py-1.5 rounded-full transition-colors">
              <RotateCcw size={12} /> Reschedule
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────
const AdminBookingsPage = () => {
  const PAGE_SIZE = 20;
  const [bookings,     setBookings]     = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,          setPage]        = useState(1);
  const [loading,      setLoading]      = useState(true);
  const [loadingMore,  setLoadingMore]  = useState(false);
  const [error,        setError]        = useState(null);
  const [searchInput,  setSearchInput]  = useState('');
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [reschedulingBooking, setReschedulingBooking] = useState(null);

  React.useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchBookings = React.useCallback(async (p = 1, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const res  = await bookingService.getAllAdmin({
        page: p, limit: PAGE_SIZE, status: statusFilter || undefined, search: search || undefined,
      });
      const body = res.data;
      const rows = Array.isArray(body?.data) ? body.data : [];
      setBookings(prev => (append ? [...prev, ...rows] : rows));
      setTotal(body?.meta?.total ?? rows.length);
      setPage(p);
    } catch {
      setError('Could not load bookings');
      if (!append) setBookings([]);
    } finally {
      setLoading(false); setLoadingMore(false);
    }
  }, [search, statusFilter]);

  React.useEffect(() => { fetchBookings(1, false); }, [fetchBookings]);

  const hasMore = bookings.length < total;
  const hasActiveFilters = !!(search || statusFilter);
  const clearFilters = () => { setSearchInput(''); setSearch(''); setStatusFilter(''); };

  return (
    <DashboardLayout title="Bookings">
      <div className="max-w-5xl space-y-5">

        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-forest-400" />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by client or expert name/email..."
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-700 focus:outline-none focus:border-forest-500 bg-white">
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="RESCHEDULED">Rescheduled</option>
          </select>
          {hasActiveFilters && (
            <button onClick={clearFilters}
              className="flex items-center gap-1.5 text-xs font-medium text-forest-500 hover:text-forest-800 px-3 py-2.5">
              <X size={13} /> Clear
            </button>
          )}
        </div>

        {!loading && (
          <p className="text-xs text-forest-400">
            {hasActiveFilters ? `${total} matching booking${total !== 1 ? 's' : ''}` : `${total} booking${total !== 1 ? 's' : ''} total`}
          </p>
        )}

        {error && (
          <div className="flex items-center gap-2 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        <div className="space-y-3">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 bg-white border border-cream-200 rounded-2xl animate-pulse" />
            ))
          ) : bookings.length === 0 ? (
            <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
              <User size={32} className="mx-auto mb-3 opacity-30" />
              <p className="font-serif text-xl text-forest-600">
                {hasActiveFilters ? 'No matching bookings' : 'No bookings yet'}
              </p>
            </div>
          ) : (
            <>
              {bookings.map(b => <BookingRow key={b.id} b={b} onReschedule={setReschedulingBooking} />)}
              {hasMore && (
                <div className="flex justify-center py-2">
                  <button onClick={() => fetchBookings(page + 1, true)} disabled={loadingMore}
                    className="text-sm text-forest-600 border border-cream-200 hover:border-forest-400 px-4 py-2 rounded-full transition-colors disabled:opacity-50 flex items-center gap-2">
                    {loadingMore && <Loader2 size={13} className="animate-spin" />}
                    Load more ({bookings.length} of {total})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {reschedulingBooking && (
        <AdminRescheduleModal
          booking={reschedulingBooking}
          onClose={() => setReschedulingBooking(null)}
          onDone={() => fetchBookings(1, false)}
        />
      )}
    </DashboardLayout>
  );
};

export default AdminBookingsPage;